import { useEffect, useMemo, useState } from "react";
import type { LocalStore, StoredDrill } from "@onchainreps/storage";
import { DrillBoard } from "./DrillBoard";
import type { DrillItem } from "../lib/drills";
import { mistakeDrill } from "../lib/drills";
import { useDrillSession } from "../lib/useDrillSession";

interface MistakeTrainerProps {
  store: LocalStore | null;
}

const CATEGORY_LABEL: Record<string, string> = {
  blunder: "Blunder",
  miss: "Missed chance",
  inaccuracy: "Inaccuracy",
};

function toDrills(rows: StoredDrill[]): DrillItem[] {
  return rows.slice(0, 40).map((row) =>
    mistakeDrill(row.fen, row.bestMove, `${CATEGORY_LABEL[row.category] ?? row.category}`, row.ply),
  );
}

export function MistakeTrainer({ store }: MistakeTrainerProps) {
  const [drills, setDrills] = useState<DrillItem[]>([]);
  const [rows, setRows] = useState<StoredDrill[]>([]);
  const session = useDrillSession(drills, store);

  useEffect(() => {
    let active = true;
    void (async () => {
      if (!store) return;
      const stored = await store.listDrills();
      if (!active) return;
      setRows(stored);
    })();
    return () => {
      active = false;
    };
  }, [store]);

  const count = useMemo(() => rows.length, [rows]);

  if (!store) {
    return (
      <section className="panel">
        <div className="panel__head">
          <h1 className="panel__title">Your mistakes</h1>
        </div>
        <div className="panel__body">
          <p className="notice">Opening local storage…</p>
        </div>
      </section>
    );
  }

  if (count === 0) {
    return (
      <section className="panel">
        <div className="panel__head">
          <h1 className="panel__title">Your mistakes</h1>
        </div>
        <div className="panel__body">
          <p className="notice">
            Analyse a game first. Every blunder, missed chance and inaccuracy is collected here, and the
            scheduler brings the worst ones back until you stop losing them.
          </p>
        </div>
      </section>
    );
  }

  if (session.current) {
    return (
      <div className="trainer">
        <div className="trainer__bar">
          <button className="btn btn--inline" onClick={() => setDrills([])}>
            Back
          </button>
          <span className="trainer__count">
            {session.index + 1} / {session.total}
          </span>
          <span className="trainer__streak">
            Streak {session.streak} · Best {session.bestStreak}
          </span>
        </div>

        <div className="progress" aria-hidden="true">
          <div
            className="progress__fill"
            style={{ width: `${session.total === 0 ? 0 : (session.index / session.total) * 100}%` }}
          />
        </div>

        <DrillBoard
          key={session.current.id}
          drill={session.current}
          onAnswer={session.answer}
          onNext={() => {
            const last = session.index + 1 >= session.total;
            session.next();
            if (last) setDrills([]);
          }}
        />
      </div>
    );
  }

  return (
    <div className="trainer">
      <section className="panel">
        <div className="panel__head">
          <h1 className="panel__title">Your mistakes</h1>
          <span className="chip">
            <span className="chip__dot" />
            {count} positions
          </span>
        </div>
        <div className="panel__body">
          <p className="notice">
            These are the positions from your own games where the evaluation dropped. Play the move the
            engine wanted and the position is fixed for good.
          </p>
          <div className="btnrow">
            <button className="btn btn--primary" onClick={() => setDrills(toDrills(rows))}>
              Drill my mistakes
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
